import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { 
  HomeIcon, 
  CalendarIcon, 
  GridIcon,
  PersonIcon,
  BellIcon,
  GroupIcon
} from '@radix-ui/react-icons';
import { Menu, LogOut, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { Box, Button, Flex, Separator } from '@radix-ui/themes';
import NotificationBell from './NotificationBell';
import ThemeToggle from './ThemeToggle';
import { supabase } from '../lib/supabase';
import { showAlert } from '../lib/alerts';

type Props = {
  isAdmin: boolean;
};

export default function Sidebar({ isAdmin }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/', icon: HomeIcon },
    { name: 'Presenze', href: '/attendance', icon: CalendarIcon },
    { name: 'Dipendenti', href: '/users', icon: GroupIcon, adminOnly: true },
    { name: 'Notifiche', href: '/notifications', icon: BellIcon },
    { name: 'Centri', href: '/centers', icon: GridIcon },
    { name: 'Gestione Sedi', href: '/admin/locations', icon: MapPin, adminOnly: true },
    { name: 'Profilo', href: '/profile', icon: PersonIcon },
  ];

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id || null);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      navigate('/');
      showAlert('Logout effettuato con successo', 'success');
    } catch (error) {
      console.error('Error during logout:', error);
      showAlert('Errore durante il logout', 'error');
    }
  };

  const filteredNavigation = navigation.filter(item => 
    !item.adminOnly || (item.adminOnly && isAdmin)
  );

  const SidebarContent = () => (
    <Flex direction="column" height="100%">
      <Box p={isCollapsed ? "2" : "4"}>
        <Flex justify="between" align="center">
          {!isCollapsed && (
            <img 
              src="https://hrpwacxomgudoclntzzi.supabase.co/storage/v1/object/public/img//logo_crs_full_grigio.svg"
              alt="CRS Logo"
              style={{ height: '40px', width: 'auto' }}
            />
          )}
          {isCollapsed && (
            <img 
              src="https://hrpwacxomgudoclntzzi.supabase.co/storage/v1/object/public/img//logo_crs_solo_red.svg"
              alt="CRS Logo"
              style={{ height: '40px', width: 'auto' }}
            />
          )}
          <Flex gap="2" align="center">
            {!isCollapsed && (
              <>
                <ThemeToggle />
                {userId && <NotificationBell userId={userId} />}
              </>
            )}
            <Button 
              variant="ghost" 
              className="md:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <Menu size={20} />
            </Button>
          </Flex>
        </Flex>
      </Box>

      <Separator size="4" />

      <Box p="2" grow="1">
        <Flex direction="column" gap="1">
          {filteredNavigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link key={item.name} to={item.href}>
                <Button 
                  variant={isActive ? 'solid' : 'ghost'} 
                  style={{ 
                    width: '100%', 
                    justifyContent: isCollapsed ? 'center' : 'flex-start'
                  }}
                >
                  <item.icon width="16" height="16" />
                  {!isCollapsed && <span style={{ marginLeft: '0.5rem' }}>{item.name}</span>}
                </Button>
              </Link>
            );
          })}
        </Flex>
      </Box>

      <Box p="2">
        {/* Desktop collapse button */}
        <Button
          variant="ghost"
          className="hidden md:flex mb-2"
          style={{ width: '100%', justifyContent: 'center' }}
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </Button>

        <Button 
          variant="soft" 
          color="red"
          style={{ 
            width: '100%',
            justifyContent: isCollapsed ? 'center' : 'flex-start'
          }}
          onClick={handleLogout}
        >
          <LogOut size={16} />
          {!isCollapsed && <span style={{ marginLeft: '0.5rem' }}>Logout</span>}
        </Button>
      </Box>
    </Flex>
  );

  return (
    <>
      <Button
        variant="ghost"
        className="fixed top-4 left-4 md:hidden z-50"
        onClick={() => setIsMobileMenuOpen(true)}
      >
        <Menu size={24} />
      </Button>

      {isMobileMenuOpen && (
        <Box
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <Box 
        className="hidden md:block"
        style={{ 
          width: isCollapsed ? '5rem' : '16rem',
          height: '100vh', 
          borderRight: '1px solid var(--gray-5)',
          position: 'fixed',
          top: 0,
          left: 0,
          backgroundColor: 'var(--color-background)',
          transition: 'width 0.3s ease'
        }}
      >
        <SidebarContent />
      </Box>

      {/* Content offset for desktop */}
      <Box 
        className="hidden md:block"
        style={{ 
          width: isCollapsed ? '5rem' : '16rem',
          flexShrink: 0
        }}
      />

      <Box
        className={`fixed inset-y-0 left-0 transform ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } md:hidden transition-transform duration-300 ease-in-out z-50`}
        style={{ 
          width: '16rem',
          backgroundColor: 'var(--color-background)',
          borderRight: '1px solid var(--gray-5)'
        }}
      >
        <SidebarContent />
      </Box>
    </>
  );
}